/**
 * Renders a `product_selection` view action (design D20): a compact card in
 * the chat (the assistant's picks per slot) whose "Review selection" opens the
 * review popup (`ProductSelectionDialog.tsx`). What is checked/added and
 * whether the selection was taken over lives in `productSelectionStore` (keyed
 * by the view), so card and popup share it and it survives chat remounts. A
 * view whose following user message is its takeover note shows that
 * selection, read-only.
 */
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { productId, type ProductIdentity } from "./productDisplay.js";
import { ProductDetails, ProductSummary } from "./productParts.js";
import {
  buildProductSelectionNote,
  findTakenOverSlots,
  initialProductSelectionState,
  isChecked,
  productSelectionKey,
  slotProducts,
  takeOverSlots,
  toggleProduct,
  withDisplayData,
  type ProductSelectionPick,
  type ProductSelectionViewSpec,
} from "./productSelectionSpec.js";
import { useProductSelectionState } from "./productSelectionStore.js";

// The popup pulls in the catalogue search, a large part of the app the chat
// itself does not need.
const ProductSelectionDialog = React.lazy(() =>
  import("./ProductSelectionDialog.js").then(module => ({
    default: module.ProductSelectionDialog,
  }))
);

export function ProductSelectionView({
  view,
  busy,
  answeredWith,
  onSend,
}: {
  view: ProductSelectionViewSpec;
  /** True while the next answer streams in. */
  busy: boolean;
  /** The following user message's text; when it is this view's takeover note, the view is read-only (survives reloads). */
  answeredWith?: string;
  /** Sends a chat message on the user's behalf. */
  onSend: (text: string) => void;
}) {
  const { t } = useTranslate(["Assistant", "Global"]);
  const stateKey = productSelectionKey(view);
  const initial = React.useMemo(
    () => initialProductSelectionState(view),
    [view]
  );
  const [state, update] = useProductSelectionState(stateKey, initial);
  const [open, setOpen] = React.useState(false);
  const [details, setDetails] = React.useState<ProductIdentity | null>(null);
  const takenSlots = findTakenOverSlots(view, answeredWith) ?? state.takenOver;
  const takenOver = takenSlots !== null;

  const takeOver = () => {
    const taken = takeOverSlots(view, state);
    update(current => ({ ...current, takenOver: taken }));
    onSend(buildProductSelectionNote(view.title, taken));
  };

  return (
    <Paper variant="outlined" sx={{ p: 1, mt: 1 }}>
      <Stack direction="row" sx={{ alignItems: "center", mb: 0.5 }}>
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          {view.title}
        </Typography>
        {takenOver && (
          <Chip size="small" color="success" label={t("Taken over")} />
        )}
      </Stack>
      {view.intent && (
        <Typography
          variant="caption"
          color="text.secondary"
          component="div"
          sx={{ mb: 0.5 }}
        >
          {view.intent}
        </Typography>
      )}
      <Stack spacing={1.5}>
        {view.slots.map(slot => {
          const taken = takenSlots?.find(s => s.key === slot.key);
          const label = taken?.label ?? slot.label;
          const products: ProductSelectionPick[] = takenSlots
            ? (taken?.products ?? []).map(product =>
                withDisplayData(view, state, slot.key, product)
              )
            : slotProducts(slot, state);
          return (
            <Box key={slot.key}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {label}
              </Typography>
              {products.length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                  {takenOver
                    ? t("No products taken over.")
                    : t("No suggestions. Use “Review selection” to search.")}
                </Typography>
              ) : (
                <Stack spacing={0.75}>
                  {products.map(product => (
                    <Stack
                      key={productId(product)}
                      direction="row"
                      sx={{
                        alignItems: "center",
                        borderRadius: 1,
                        pr: 0.5,
                        "&:hover": { bgcolor: "action.hover" },
                      }}
                    >
                      {!takenOver && (
                        <Checkbox
                          size="small"
                          checked={isChecked(slot, state, product)}
                          onChange={() =>
                            update(current =>
                              toggleProduct(current, slot, product)
                            )
                          }
                          slotProps={{
                            input: {
                              "aria-label": t("Select {title}", {
                                title: product.title,
                                ns: "Assistant",
                              }),
                            },
                          }}
                        />
                      )}
                      <ProductSummary
                        product={product}
                        reason={product.reason}
                        lookupMissing={takenOver}
                        onOpen={() => setDetails(product)}
                      />
                    </Stack>
                  ))}
                </Stack>
              )}
            </Box>
          );
        })}
      </Stack>
      {!takenOver && (
        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
          <Button
            variant="contained"
            size="small"
            disabled={busy}
            onClick={takeOver}
          >
            {t("Take over selection")}
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={busy}
            onClick={() => setOpen(true)}
          >
            {t("Review selection")}
          </Button>
        </Stack>
      )}
      {open && !takenOver && (
        <React.Suspense fallback={null}>
          <ProductSelectionDialog
            view={view}
            busy={busy}
            onClose={() => setOpen(false)}
            onSend={onSend}
          />
        </React.Suspense>
      )}
      {details && (
        <ProductDetails product={details} onClose={() => setDetails(null)} />
      )}
    </Paper>
  );
}
